import { useEffect, useState } from 'react';
import Script from 'next/script';
import useCookieConsent from 'hooks/useCookieConsent';
import { trackingAllowed } from 'utils/cookieConsent';

// Google Tag Manager and the Facebook pixel block the main thread for several
// hundred milliseconds on load. They are loaded on the visitor's first
// interaction, a few seconds after the page has settled, or when the tab is
// hidden, whichever comes first. Visits shorter than that may go uncounted;
// that trade-off was accepted by marketing.
// "Settled" is the window load event plus the browser's next idle period. A
// timer counted from mount put the scripts inside PageSpeed's measuring window
// on slow phones, where start-up runs past six seconds; counted from settling,
// they land after the five quiet seconds that end that window.
const INTERACTION_EVENTS = [
  'scroll',
  'pointerdown',
  'keydown',
  'touchstart',
] as const;
const FALLBACK_DELAY_MS = 6000;
// Longest wait for an idle period once the page has loaded.
const IDLE_TIMEOUT_MS = 5000;

const useLoadAfterEngagement = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    const load = () => setReady(true);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') load();
    };
    // Already in a background tab: load now, since timers are throttled there.
    if (document.visibilityState === 'hidden') {
      load();
      return;
    }
    let timer: number | undefined;
    let idleHandle: number | undefined;
    const startTimer = () => {
      timer = window.setTimeout(load, FALLBACK_DELAY_MS);
    };
    const onSettled = () => {
      if ('requestIdleCallback' in window) {
        idleHandle = window.requestIdleCallback(startTimer, {
          timeout: IDLE_TIMEOUT_MS,
        });
      } else {
        startTimer();
      }
    };
    if (document.readyState === 'complete') onSettled();
    else window.addEventListener('load', onSettled, { once: true });
    // Capture phase, so scrolling inside a scrollable element (whose scroll
    // event does not bubble) counts too.
    INTERACTION_EVENTS.forEach(event =>
      window.addEventListener(event, load, {
        once: true,
        passive: true,
        capture: true,
      }),
    );
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('load', onSettled);
      if (idleHandle !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleHandle);
      }
      window.clearTimeout(timer);
      INTERACTION_EVENTS.forEach(event =>
        window.removeEventListener(event, load, { capture: true }),
      );
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [ready]);

  return ready;
};

// Both trackers set analytics / advertising cookies, so they load only after
// the visitor accepts them in the cookie banner (WHIT-421).
const DeferredTracking: React.FC = () => {
  const ready = useLoadAfterEngagement();
  const { consent } = useCookieConsent();
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
  const pixelId = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;

  if (!ready || !trackingAllowed(consent)) return null;

  // The setup snippets are files under public/tracking, not inline scripts:
  // the Content-Security-Policy allows them through 'self', so they need no
  // nonce and also run on the prerendered 404 and after navigating from it
  // (WHIT-406).
  return (
    <>
      {gtmId && (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${gtmId}`}
          />
          <Script
            id="gtm-script"
            strategy="afterInteractive"
            src="/tracking/google-tag.js"
            data-id={gtmId}
          />
        </>
      )}
      {pixelId && (
        <Script
          id="facebook-pixel"
          strategy="afterInteractive"
          src="/tracking/meta-pixel.js"
          data-id={pixelId}
        />
      )}
    </>
  );
};

export default DeferredTracking;
